import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, apiErrorMessage } from '../../api/client'
import { Users, BookOpen, GraduationCap, Shield, Trash2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'

// ── Types ──────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string
  username: string
  email: string
  role: string
  is_active: boolean
  email_verified: boolean
  created_at: string
}

interface AdminCourse {
  id: string
  title: string
  slug: string
  level: string
  is_published: boolean
  instructor_id: string
  created_at: string
}

interface Stats {
  total_users: number
  total_courses: number
  published_courses: number
  total_enrollments: number
}

// ── Helpers ────────────────────────────────────────────────────────────────

const ROLE_COLOURS: Record<string, string> = {
  admin:      'bg-orange-900/60 text-orange-300',
  instructor: 'bg-blue-900/60 text-blue-300',
  learner:    'bg-slate-800 text-slate-400',
}

const LEVEL_COLOURS: Record<string, string> = {
  beginner:     'bg-green-900/50 text-green-300',
  intermediate: 'bg-yellow-900/50 text-yellow-300',
  advanced:     'bg-red-900/50 text-red-300',
}

function Badge({ text, colour }: { text: string; colour: string }) {
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${colour}`}>{text}</span>
}

// ── Admin Dashboard Page ───────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const [tab, setTab] = useState<'stats' | 'users' | 'courses'>('stats')

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Shield size={28} className="text-orange-400" />
        <h1 className="text-2xl font-extrabold">Admin Dashboard</h1>
      </div>

      {/* Tab bar */}
      <div className="flex gap-0 border-b border-brand-border mb-6">
        {([
          ['stats',   'Overview'],
          ['users',   'Users'],
          ['courses', 'Courses'],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-orange-400 text-orange-300'
                : 'border-transparent text-brand-muted hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'stats'   && <StatsTab />}
      {tab === 'users'   && <UsersTab />}
      {tab === 'courses' && <CoursesTab />}
    </div>
  )
}

// ── Stats Tab ──────────────────────────────────────────────────────────────

function StatsTab() {
  const { data, isLoading } = useQuery<Stats>({
    queryKey: ['admin-stats'],
    queryFn: () => adminApi.getStats().then(r => r.data),
  })

  if (isLoading) return <Spinner />

  const cards = [
    { icon: <Users size={22} className="text-blue-400"/>,       label: 'Total Users',        value: data?.total_users ?? 0 },
    { icon: <BookOpen size={22} className="text-green-400"/>,   label: 'Total Courses',      value: data?.total_courses ?? 0 },
    { icon: <Eye size={22} className="text-brand-blue"/>,       label: 'Published Courses',  value: data?.published_courses ?? 0 },
    { icon: <GraduationCap size={22} className="text-amber-400"/>, label: 'Enrollments',    value: data?.total_enrollments ?? 0 },
  ]

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {cards.map(c => (
        <div key={c.label} className="card flex items-center gap-4">
          <div className="p-3 rounded-xl bg-brand-surface2">{c.icon}</div>
          <div>
            <p className="text-2xl font-extrabold">{c.value}</p>
            <p className="text-sm text-brand-muted">{c.label}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Users Tab ──────────────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')

  const { data: users = [], isLoading, error, refetch } = useQuery<AdminUser[]>({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers().then(r => r.data),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof adminApi.updateUser>[1] }) =>
      adminApi.updateUser(id, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Update failed'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => { toast.success('User deleted'); qc.invalidateQueries({ queryKey: ['admin-users'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Delete failed'),
  })

  const filtered = users.filter(u =>
    !search || u.username.includes(search) || u.email.includes(search)
  )

  if (isLoading) return <Spinner />
  if (error) return (
    <div className="card" role="alert">
      <p>{apiErrorMessage(error, 'Could not load users. The user count is unavailable.')}</p>
      <button className="btn-primary mt-3" onClick={() => refetch()}>Retry</button>
    </div>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-brand-muted">{users.length} users total</p>
        <input
          type="text"
          placeholder="Search by username or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input text-sm py-1.5 px-3 w-64"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border text-xs text-brand-muted uppercase tracking-wide">
              <th className="text-left py-2 px-3">User</th>
              <th className="text-left py-2 px-3">Role</th>
              <th className="text-left py-2 px-3">Verified</th>
              <th className="text-left py-2 px-3">Active</th>
              <th className="text-left py-2 px-3">Joined</th>
              <th className="text-left py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-b border-brand-border/40 hover:bg-brand-surface2/50 transition-colors">
                <td className="py-2.5 px-3">
                  <p className="font-semibold">{u.username}</p>
                  <p className="text-xs text-brand-muted">{u.email}</p>
                </td>
                <td className="py-2.5 px-3">
                  <RoleSelector
                    userId={u.id}
                    current={u.role}
                    onChange={role => updateMut.mutate({ id: u.id, patch: { role } })}
                  />
                </td>
                <td className="py-2.5 px-3">
                  <ToggleIcon
                    value={u.email_verified}
                    onToggle={() => updateMut.mutate({ id: u.id, patch: { email_verified: !u.email_verified } })}
                    title={u.email_verified ? 'Verified — click to unverify' : 'Not verified — click to verify'}
                  />
                </td>
                <td className="py-2.5 px-3">
                  <ToggleIcon
                    value={u.is_active}
                    onToggle={() => updateMut.mutate({ id: u.id, patch: { is_active: !u.is_active } })}
                    title={u.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                  />
                </td>
                <td className="py-2.5 px-3 text-brand-muted">{u.created_at.slice(0, 10)}</td>
                <td className="py-2.5 px-3">
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) {
                        deleteMut.mutate(u.id)
                      }
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-900/30"
                    title="Delete user"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-brand-muted">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Courses Tab ────────────────────────────────────────────────────────────

function CoursesTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')

  const { data: courses = [], isLoading } = useQuery<AdminCourse[]>({
    queryKey: ['admin-courses'],
    queryFn: () => adminApi.listCourses().then(r => r.data),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { is_published?: boolean } }) =>
      adminApi.updateCourse(id, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-courses'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Update failed'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteCourse(id),
    onSuccess: () => { toast.success('Course deleted'); qc.invalidateQueries({ queryKey: ['admin-courses'] }); qc.invalidateQueries({ queryKey: ['admin-stats'] }) },
    onError: (e: any) => toast.error(e.response?.data?.detail || 'Delete failed'),
  })

  const filtered = courses.filter(c =>
    !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.slug.includes(search)
  )

  if (isLoading) return <Spinner />

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-brand-muted">{courses.length} courses total</p>
        <input
          type="text"
          placeholder="Search by title or slug…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input text-sm py-1.5 px-3 w-64"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-border text-xs text-brand-muted uppercase tracking-wide">
              <th className="text-left py-2 px-3">Course</th>
              <th className="text-left py-2 px-3">Level</th>
              <th className="text-left py-2 px-3">Published</th>
              <th className="text-left py-2 px-3">Created</th>
              <th className="text-left py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-brand-border/40 hover:bg-brand-surface2/50 transition-colors">
                <td className="py-2.5 px-3">
                  <p className="font-semibold">{c.title}</p>
                  <p className="text-xs text-brand-muted font-mono">{c.slug}</p>
                </td>
                <td className="py-2.5 px-3">
                  <Badge text={c.level} colour={LEVEL_COLOURS[c.level] || 'bg-slate-800 text-slate-400'} />
                </td>
                <td className="py-2.5 px-3">
                  <button
                    onClick={() => updateMut.mutate({ id: c.id, patch: { is_published: !c.is_published } })}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full transition-colors ${
                      c.is_published
                        ? 'bg-green-900/50 text-green-300 hover:bg-green-900'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                    title={c.is_published ? 'Click to unpublish' : 'Click to publish'}
                  >
                    {c.is_published ? <><Eye size={12}/> Published</> : <><EyeOff size={12}/> Draft</>}
                  </button>
                </td>
                <td className="py-2.5 px-3 text-brand-muted">{c.created_at.slice(0, 10)}</td>
                <td className="py-2.5 px-3 flex items-center gap-2">
                  <a
                    href={`/learn/${c.slug}`}
                    className="text-brand-blue hover:underline text-xs"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </a>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete course "${c.title}"? This cannot be undone.`)) {
                        deleteMut.mutate(c.id)
                      }
                    }}
                    className="text-red-400 hover:text-red-300 transition-colors p-1 rounded hover:bg-red-900/30"
                    title="Delete course"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-brand-muted">No courses found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Small shared components ────────────────────────────────────────────────

function RoleSelector({ current, onChange }: {
  userId?: string
  current: string
  onChange: (role: string) => void
}) {
  return (
    <select
      value={current}
      onChange={e => onChange(e.target.value)}
      className={`text-xs px-2 py-1 rounded-full border-0 font-semibold cursor-pointer ${ROLE_COLOURS[current] || 'bg-slate-800 text-white'}`}
      title={`Current role: ${current}`}
    >
      <option value="learner">learner</option>
      <option value="instructor">instructor</option>
      <option value="admin">admin</option>
    </select>
  )
}

function ToggleIcon({ value, onToggle, title }: { value: boolean; onToggle: () => void; title: string }) {
  return (
    <button onClick={onToggle} title={title} className="transition-opacity hover:opacity-70">
      {value
        ? <CheckCircle size={17} className="text-green-400" />
        : <XCircle size={17} className="text-slate-500" />
      }
    </button>
  )
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}
